import { CalendarToday, PersonAdd, AttachMoney } from "@mui/icons-material";
import { Button, IconButton, Stack, Typography, Tooltip } from "@mui/material";
import {
  DateCalendar,
  LocalizationProvider,
  MobileDatePicker,
} from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import "dayjs/locale/pt-br";
import { Dispatch, SetStateAction, useEffect, useState } from "react";

import { isMobile } from "react-device-detect";
import { useNavigate } from "react-router-dom";
import { useContractContext } from "../../contexts/ContractContext";
import { useLocationContext } from "../../contexts/LocationContext";
import { usePractitionerRoleContext } from "../../contexts/PractitionerRoleContext";
import { useSessionContext } from "../../contexts/SessionContext";
import { CreateAppointmentDialog } from "../../pages/schedulemanager/components/creatappointmentdialog";
import { DateFilterType } from "../../utils/interfaces";
import { useFhirData } from "../../utils/useFhirData";
import { SecondaryButton } from "../common";
import { CommonDialog } from "../common/CommonDialog";
import { DataSkeleton } from "../common/DataSkeleton";
import { NoDataWarning } from "../common/NoDataWarning";
import VideoConferencia from "../videoConferencia/VideoConferencia";
import { AppointmentDetails } from "./AppointmentDetails";
import { CardSchedule } from "./CardSchedule";
import DateFilter from "./DateFilter";
import { ScheduleCardsMobile } from "./ScheduleCardsMobile";
import { marketplaceService } from "@/services/marketplace/MarketplaceService";

interface IProps {
  bundle: any | undefined;
  hasActiveSchedule?: any;
  relatedPatient?: any;
  dateFilter: Dayjs;
  setDateFilter: Dispatch<SetStateAction<Dayjs>>;
  filterType: DateFilterType;
  setFilterType: Dispatch<SetStateAction<DateFilterType>>;
  setRefetch: Dispatch<SetStateAction<boolean>>;
  refetch: boolean;
  loading: boolean;
}

export function Schedule({
  hasActiveSchedule,
  bundle,
  relatedPatient,
  dateFilter,
  filterType,
  setDateFilter,
  setFilterType,
  loading,
  setRefetch,
  refetch,
}: IProps) {
  const [createAppointmentDialogOpen, setCreateAppointmentDialogOpen] =
    useState<boolean>(false);
  const { access, userPatient } = useSessionContext();
  const { userPractitioner } = usePractitionerRoleContext();
  const { location, setIdsOrganizations } = useLocationContext();
  const { currentContract } = useContractContext();
  const { extractAppointmentData } = useFhirData;

  const [openDatePicker, setOpenDatePicker] = useState(false);
  const [openDatePickerMobile, setOpenDatePickerMobile] = useState(false);
  const [dateFieldValue, setDateFieldValue] = useState(dayjs());
  const [selectedCard, setSelectedCard] = useState(0);
  const [isWalkin, setIsWalkin] = useState(false);
  const [isShortCutAppointment, setIsShortCutAppointment] = useState(false);
  const navigate = useNavigate();

  const [openAppointmentDetails, setOpenAppointmentDetails] = useState(false);
  const [hasPaymentLinks, setHasPaymentLinks] = useState<{
    [key: string]: string;
  }>({});
  const [checkedSlots, setCheckedSlots] = useState<{ [key: string]: boolean }>(
    {}
  );
  const [isCheckingPaymentLinks, setIsCheckingPaymentLinks] =
    useState<boolean>(false);

  const isAppointmentBundleEntry = (
    entry: fhir5.BundleEntry<fhir5.Appointment | fhir5.Patient | fhir5.Location>
  ): entry is fhir5.BundleEntry<fhir5.Appointment> =>
    entry.resource?.resourceType === "Appointment";

  const isPatientBundleEntry = (entry: any): any => {
    return entry.resource?.resourceType === "Patient";
  };

  const isLocationBundleEntry = (
    entry: fhir5.BundleEntry<fhir5.Appointment | fhir5.Patient | fhir5.Location>
  ): entry is fhir5.BundleEntry<fhir5.Location> =>
    entry.resource?.resourceType === "Location";

  const patientsBundle = bundle?.entry?.filter(isPatientBundleEntry);

  const locationsBundle = bundle?.entry?.filter(isLocationBundleEntry);

  const appointmentsBundle = bundle?.entry
    ?.filter(isAppointmentBundleEntry)
    ?.filter((el: any) =>
      access?.type === "professional"
        ? true
        : el.resource?.end && new Date(el.resource?.end) > new Date(Date.now())
    );

  const locationReferences = appointmentsBundle
    ?.map((appointment: any) => appointment?.resource?.participant)
    .flat()
    .filter((participant: any) =>
      participant?.actor?.reference?.startsWith("Location/")
    )
    .map((participant: any) =>
      participant?.actor?.reference?.replace("Location/", "")
    )
    .filter(
      (reference: any, index: number, references: any) =>
        references.indexOf(reference) === index
    )
    .join(",");

  const selectedCardData = [
    appointmentsBundle?.[selectedCard]?.resource,
    locationsBundle?.filter((locationEl: any) => {
      const selectedAppointment = appointmentsBundle?.[selectedCard];
      if (selectedAppointment) {
        return (
          locationEl.resource?.id ===
          appointmentsBundle[
            selectedCard
          ]?.resource?.participant?.[1].actor?.reference?.split("/")[1]
        );
      }
      return false;
    })?.[0]?.resource,
  ].filter((el) => el !== undefined);

  const selectedCardTeleconsulting = useFhirData.virtualService(
    location?.find((loc: any) =>
      loc?.resource?.id.includes(selectedCardData?.[1]?.id)
    )?.resource
  );

  /**
   * Canal de atendimento do card selecionado (ex: "chat", "teleatendimento").
   * Lido da extension attendance-channel da Location correspondente.
   */
  const selectedCardAttendanceChannel = location
    ?.find((loc: any) =>
      loc?.resource?.id.includes(selectedCardData?.[1]?.id)
    )
    ?.resource?.extension?.find((ext: any) =>
      ext.url?.includes("attendance-channel")
    )?.valueCode as string | undefined;

  const virtualServiceUrl = selectedCardData?.find(
    (el: any) => el?.resourceType === "Appointment"
  )?.virtualService?.[0]?.addressUrl;

  useEffect(() => {
    setSelectedCard(0);
  }, [dateFilter]);

  useEffect(() => {
    setSelectedCard(0);
  }, [refetch]);

  useEffect(() => {
    setDateFilter(dayjs(dateFieldValue));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateFieldValue]);

  useEffect(() => {
    if (access?.type === "patient")
      setIdsOrganizations(`${locationReferences}`);
  }, [locationReferences, setIdsOrganizations, access]);

  useEffect(() => {
    const checkPaymentLinks = async () => {
      if (isCheckingPaymentLinks || !appointmentsBundle?.length) return;

      setIsCheckingPaymentLinks(true);

      const newPaymentStatuses = { ...hasPaymentLinks };
      const newCheckedSlots = { ...checkedSlots };
      let hasChanges = false;

      try {
        const pendingAppointments = appointmentsBundle.filter(
          (appointment: { resource?: fhir5.Appointment }) => {
            const slotReference = appointment?.resource?.slot?.[0]?.reference;
            if (!slotReference || appointment?.resource?.status !== "pending")
              return false;

            const slotId = slotReference.split("/")?.[1];
            return slotId && !newCheckedSlots[slotId];
          }
        );

        if (pendingAppointments.length === 0) {
          setIsCheckingPaymentLinks(false);
          return;
        }

        for (const appointment of pendingAppointments) {
          const slotReference = appointment?.resource?.slot?.[0]?.reference;
          const slotId = slotReference.split("/")?.[1];

          if (slotId) {
            newCheckedSlots[slotId] = true;
            hasChanges = true;

            try {
              const paymentLink =
                await marketplaceService.getPaymentLinkBySlotId(slotId);
              newPaymentStatuses[appointment.resource.id] = paymentLink;
            } catch (error) {
              console.error(
                `Erro ao verificar link de pagamento para slot ${slotId}:`,
                error
              );
              newPaymentStatuses[appointment.resource.id] = "";
            }
          }
        }
      } catch (error) {
        console.error(
          "Erro ao processar verificação de links de pagamento:",
          error
        );
      }

      if (hasChanges) {
        setHasPaymentLinks(newPaymentStatuses);
        setCheckedSlots(newCheckedSlots);
      }

      setIsCheckingPaymentLinks(false);
    };

    if (access?.type === "patient") {
      checkPaymentLinks();
    }
  }, [
    appointmentsBundle,
    access?.type,
    checkedSlots,
    hasPaymentLinks,
    isCheckingPaymentLinks,
  ]);

  const renderPaymentButton = (appointmentData: any) => {
    if (
      access?.type !== "patient" ||
      appointmentData?.status !== "pending" ||
      !hasPaymentLinks[appointmentData?.id]
    ) {
      return null;
    }

    return (
      <Tooltip title="Realizar pagamento">
        <Button
          variant="contained"
          size="small"
          sx={{
            backgroundColor: "neutral600.main",
            "&:hover": {
              backgroundColor: "neutral700.main",
            },
            whiteSpace: "nowrap",
            minWidth: "fit-content",
            height: "40px",
            borderRadius: "8px",
            textTransform: "capitalize",
          }}
          onClick={() => {
            const paymentLink = hasPaymentLinks[appointmentData?.id];
            if (paymentLink) {
              window.open(paymentLink, "_blank");
            } else {
              console.error("Link de pagamento não disponível");
            }
          }}
        >
          <AttachMoney />
          <Typography variant="body2">Realizar pagamento</Typography>
        </Button>
      </Tooltip>
    );
  };

  return (
    <>
      {isMobile ? (
        <Stack
          direction="column"
          sx={{
            width: "100%",
            maxWidth: "100vw",
            height: "auto",
            padding: { xs: "12px", sm: "16px" },
            boxSizing: "border-box",
            overflow: "visible",
          }}
          gap={2}
        >
          {/* Header melhorado */}
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{
              padding: "12px 0",
              borderBottom: "1px solid rgba(0,0,0,0.08)",
            }}
          >
            <Typography
              variant="h6"
              fontWeight={600}
              sx={{
                color: "neutral900.main",
                fontSize: "16px",
              }}
            >
              Consultas agendadas
            </Typography>
            <Stack direction="row" gap={1} alignItems="center">
              <Typography
                variant="body2"
                fontWeight={500}
                sx={{
                  color: "neutral700.main",
                  fontSize: "14px",
                }}
              >
                {dateFieldValue.format("MMM YYYY")}
              </Typography>
              <IconButton
                sx={{
                  height: 36,
                  width: 36,
                  backgroundColor: "rgba(0,0,0,0.04)",
                  "&:hover": {
                    backgroundColor: "rgba(0,0,0,0.08)",
                  },
                  "&:active": {
                    transform: "scale(0.95)",
                  },
                }}
                onClick={() => setOpenDatePickerMobile((prev) => !prev)}
              >
                <CalendarToday
                  sx={{
                    color: "neutral700.main",
                    fontSize: 18,
                  }}
                />
              </IconButton>
              <LocalizationProvider
                dateAdapter={AdapterDayjs}
                adapterLocale="pt-br"
              >
                <MobileDatePicker
                  open={openDatePickerMobile}
                  format="MM/YYYY"
                  views={["month", "year"]}
                  onOpen={() => setOpenDatePickerMobile(true)}
                  onClose={() => setOpenDatePickerMobile(false)}
                  sx={{ display: "none" }}
                  value={dateFieldValue}
                  slotProps={{
                    dialog: {
                      fullScreen: false,
                      maxWidth: "sm",
                      PaperProps: {
                        sx: {
                          marginTop: "64px", // Espaço para o header
                          maxHeight: "calc(100vh - 64px)", // Altura máxima considerando o header
                        },
                      },
                    },
                    actionBar: {
                      actions: ["cancel", "accept"],
                    },
                  }}
                  localeText={{
                    cancelButtonLabel: "Cancelar",
                    okButtonLabel: "Confirmar",
                    toolbarTitle: "Selecionar data",
                  }}
                  onChange={(e) => setDateFieldValue(dayjs(e))}
                />
              </LocalizationProvider>
            </Stack>
          </Stack>

          {/* Container de cards com scroll melhorado */}
          <Stack
            direction="row"
            sx={{
              width: "100%",
              overflowX: "auto",
              overflowY: "hidden",
              paddingY: 1,
              paddingRight: appointmentsBundle?.length > 1 ? "16px" : "0px",
              "&::-webkit-scrollbar": {
                height: "6px",
              },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: "primary700.main",
                borderRadius: "3px",
              },
              "&::-webkit-scrollbar-track": {
                backgroundColor: "rgba(0,0,0,0.1)",
                borderRadius: "3px",
              },
              "& > *": {
                flexShrink: 0,
                width: appointmentsBundle?.length === 1 ? "100%" : "auto",
                minWidth: appointmentsBundle?.length === 1 ? "100%" : "280px",
              },
            }}
            gap={1}
          >
            {loading ? (
              <DataSkeleton />
            ) : appointmentsBundle?.length !== 0 &&
              appointmentsBundle !== undefined ? (
              appointmentsBundle.map((el: any, index: number) => {
                const dependente = relatedPatient?.find(
                  (e: any) =>
                    e.id ===
                    el.resource?.participant?.[0].actor?.reference?.split(
                      "/"
                    )[1]
                );
                const patient = patientsBundle?.find(
                  (patientEl: any) =>
                    patientEl.resource?.id ===
                    el.resource?.participant?.[0].actor?.reference?.split(
                      "/"
                    )[1] && patientEl?.resource?.name
                );

                const location = locationsBundle?.filter(
                  (locationEl: any) =>
                    locationEl.resource?.id ===
                    el.resource?.participant?.[1].actor?.reference?.split(
                      "/"
                    )[1]
                );

                const auxPatient =
                  access?.type === "patient"
                    ? patient || dependente || userPatient
                    : patient;
                return (
                  <ScheduleCardsMobile
                    key={index}
                    data={extractAppointmentData(el, auxPatient, location?.[0])}
                    selected={selectedCardData}
                    setRefetch={setRefetch}
                    onClick={() => setSelectedCard(index)}
                  />
                );
              })
            ) : (
              <Stack
                marginTop={1}
                alignItems="center"
                spacing={2}
                sx={{
                  width: "100%",
                  padding: "24px 12px",
                  backgroundColor: "rgba(0,0,0,0.02)",
                  borderRadius: 3,
                }}
              >
                <NoDataWarning message="Não há consultas agendadas para exibir" />
                <SecondaryButton
                  width="100%"
                  height="48px"
                  disabled={false}
                  onClick={() => navigate("/schedulingsearch")}
                >
                  Agendar uma consulta
                </SecondaryButton>
              </Stack>
            )}
          </Stack>
        </Stack>
      ) : (
        <Stack
          direction={{ sx: "column", md: "row" }}
          spacing={3}
          width="100%"
          maxHeight={access?.type === "patient" ? "55vh" : "100%"}
          sx={{ alignItems: "flex-start" }}
        >
          <Stack direction="column" width="100%" spacing={1}>
            <Stack height={access.type === "professional" ? "360px" : "52vh"}>
              <Stack
                direction="row"
                justifyContent="center"
                alignItems="center"
                spacing={2}
              >
                <Typography
                  sx={{ color: "neutral1000.main" }}
                  variant="h5"
                  fontWeight={600}
                  textAlign="center"
                >
                  Consultas agendadas
                </Typography>
              </Stack>
              {openDatePicker && (
                <LocalizationProvider
                  dateAdapter={AdapterDayjs}
                  adapterLocale="pt-br"
                >
                  <DateCalendar
                    sx={{
                      position: "absolute",
                      backgroundColor: "neutral0.main",
                      zIndex: "1000",
                      marginLeft: { lg: "110px", xs: "0px" },
                    }}
                    value={dateFilter}
                    onMonthChange={(e) => {
                      setFilterType("day");
                    }}
                    onChange={(e) => {
                      if (e) setDateFilter(e);
                      setFilterType("day");
                      setOpenDatePicker(false);
                    }}
                    minDate={dayjs()}
                  />
                </LocalizationProvider>
              )}
              <DateFilter
                dateFilter={dateFilter}
                setDateFilter={setDateFilter}
                filterType={filterType}
                setFilterType={setFilterType}
              />
              <Stack
                padding="0px 5px 0px 0px"
                mb={1}
                sx={{
                  overflow: "auto",
                  "&::-webkit-scrollbar": {
                    width: "0.5em",
                  },
                  "&::-webkit-scrollbar-thumb": {
                    backgroundColor: "primary700.main",
                  },
                }}
              >
                {loading ? (
                  <DataSkeleton />
                ) : appointmentsBundle?.length !== 0 &&
                  appointmentsBundle !== undefined ? (
                  appointmentsBundle.map((el: any, index: number) => {
                    const dependente = relatedPatient?.find(
                      (e: any) =>
                        e.id ===
                        el.resource?.participant?.[0].actor?.reference?.split(
                          "/"
                        )[1]
                    );
                    const patient = patientsBundle?.find(
                      (patientEl: any) =>
                        patientEl.resource?.id ===
                        el.resource?.participant?.[0].actor?.reference?.split(
                          "/"
                        )[1] && patientEl?.resource?.name
                    );

                    const locationId = locationsBundle?.filter(
                      (locationEl: any) =>
                        locationEl.resource?.id ===
                        el.resource?.participant?.[1].actor?.reference?.split(
                          "/"
                        )[1]
                    );

                    // Localização completa com extensions para este agendamento
                    const cardLocationResource = location?.find((loc: any) =>
                      loc?.resource?.id.includes(
                        locationId?.[0]?.resource?.id
                      )
                    )?.resource;

                    const locationTeleconsulting =
                      useFhirData.virtualService(cardLocationResource);

                    /**
                     * Canal de atendimento desta agenda (ex: "chat", "teleatendimento").
                     * Lido da extension attendance-channel da Location do card.
                     */
                    const cardAttendanceChannel =
                      cardLocationResource?.extension?.find((ext: any) =>
                        ext.url?.includes("attendance-channel")
                      )?.valueCode as string | undefined;

                    const auxPatient =
                      access?.type === "patient"
                        ? patient || dependente || userPatient
                        : patient;

                    const appointmentData = extractAppointmentData(
                      el,
                      auxPatient,
                      location?.[0]
                    );

                    const hideTeleconsultForPayment =
                      access?.type === "patient" &&
                      appointmentData?.status === "pending" &&
                      !!hasPaymentLinks[appointmentData?.id];

                    return (
                      <CardSchedule
                        key={index}
                        data={appointmentData}
                        locationTeleconsulting={locationTeleconsulting}
                        locationAttendanceChannel={cardAttendanceChannel}
                        hideTeleconsultBecausePaymentPending={
                          hideTeleconsultForPayment
                        }
                        selected={selectedCard}
                        setOpenAppointmentDetails={setOpenAppointmentDetails}
                        index={index}
                        onClick={() => {
                          setSelectedCard(index);
                        }}
                      />
                    );
                  })
                ) : (
                  <>
                    {access?.type === "patient" ? (
                      <Stack alignItems="center" spacing={2}>
                        <NoDataWarning message="Não há consultas agendadas para exibir" />
                        <SecondaryButton
                          width="auto"
                          height="50px"
                          disabled={false}
                          onClick={() => navigate("/schedulingsearch")}
                        >
                          Agendar uma consulta
                        </SecondaryButton>
                      </Stack>
                    ) : (
                      <>
                        <NoDataWarning />
                      </>
                    )}
                  </>
                )}
              </Stack>

              {/* Ações para pacientes - Apenas em desktop (não exibidas em mobile) */}
              {access?.type === "patient" && !isMobile && (
                <Stack
                  direction="column"
                  spacing={2}
                  justifyContent="flex-end"
                  mt={1}
                >
                  {selectedCardData?.[0]?.id &&
                    renderPaymentButton(selectedCardData[0])}
                  {selectedCardData?.[0]?.id &&
                    selectedCardTeleconsulting &&
                    !(
                      selectedCardData[0].status === "pending" &&
                      !!hasPaymentLinks[selectedCardData[0].id]
                    ) && (
                    <VideoConferencia
                      roomUrl={virtualServiceUrl}
                      attendanceType={selectedCardAttendanceChannel}
                      appointmentData={{
                        id: selectedCardData[0].id,
                        patientName: selectedCardData[0].patientName,
                        practitionerName: selectedCardData[0].practitioner,
                        startTime: selectedCardData[0].startTime,
                      }}
                    />
                  )}
                </Stack>
              )}
            </Stack>

            <Stack spacing={1}>
              {userPractitioner ? (
                <Stack spacing={1}>
                  {currentContract ? (
                    <>
                      {hasActiveSchedule ? (
                        <>
                          <Button
                            sx={{
                              gap: 1,
                              alignItems: "center",
                              justifyContent: "center",
                              borderRadius: 3,
                              fontSize: 16,
                              textTransform: "none",
                              color: "neutral0.main",
                              backgroundColor: "primary700.main",
                              ":hover": {
                                backgroundColor: "primary800.main",
                              },
                            }}
                            onClick={() => {
                              setCreateAppointmentDialogOpen(true);
                              setIsWalkin(true);
                            }}
                          >
                            <PersonAdd /> Realizar encaixe
                          </Button>
                        </>
                      ) : (
                        <SecondaryButton
                          width="auto"
                          height="50px"
                          disabled={false}
                          onClick={() => navigate("/newschedule")}
                        >
                          Crie uma agenda
                        </SecondaryButton>
                      )}
                    </>
                  ) : (
                    <></>
                  )}
                </Stack>
              ) : (
                <Stack height="38px" />
              )}
              {createAppointmentDialogOpen && (
                <CreateAppointmentDialog
                  isOpen={createAppointmentDialogOpen}
                  setModalOpen={setCreateAppointmentDialogOpen}
                  setRefetchDashboard={setRefetch}
                  cpfT={""}
                  walkin={isWalkin}
                  isShortCutAppointment={isShortCutAppointment}
                />
              )}
            </Stack>
          </Stack>
          {access.type === "professional" ? (
            <Stack direction="column" width="100%" justifyContent="center">
              <AppointmentDetails
                data={selectedCardData}
                patient={patientsBundle?.find(
                  (patientEl: fhir5.BundleEntry<fhir5.Patient> | undefined) =>
                    patientEl?.resource?.id ===
                    appointmentsBundle?.[
                      selectedCard
                    ]?.resource?.participant?.[0].actor?.reference?.split(
                      "/"
                    )[1] && patientEl?.resource?.name
                )}
                setRefetch={setRefetch}
              />
            </Stack>
          ) : (
            <CommonDialog
              isOpen={openAppointmentDetails}
              handleClose={() => setOpenAppointmentDetails(false)}
            >
              <Stack mt={3}>
                <AppointmentDetails
                  data={selectedCardData}
                  patient={patientsBundle?.find(
                    (patientEl: fhir5.BundleEntry<fhir5.Patient> | undefined) =>
                      patientEl?.resource?.id ===
                      appointmentsBundle?.[
                        selectedCard
                      ]?.resource?.participant?.[0].actor?.reference?.split(
                        "/"
                      )[1] && patientEl?.resource?.name
                  )}
                  setRefetch={setRefetch}
                />
              </Stack>
            </CommonDialog>
          )}
        </Stack>
      )}
    </>
  );
}
